import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, GraduationCap, Compass, MapPin, Sprout, Database, Microscope, Layers, Briefcase, Download, Check } from "lucide-react";
import { IMG } from "../data/content";
import Seo from "../components/Seo";
import api, { formatApiErrorDetail } from "../lib/api";

const MODULES = [
  {
    icon: Compass,
    title: "Understanding Native Seed Systems",
    items: [
      "The role of seed in restoration and rehabilitation",
      "Why seed supply matters",
      "Restoration project life cycles",
      "Seed demand across carbon, biodiversity and rehabilitation projects",
    ],
  },
  {
    icon: MapPin,
    title: "Seed Collection Planning",
    items: [
      "Understanding target species",
      "Seasonal planning",
      "Collection permits and approvals",
      "Working with Traditional Owners and cultural protocols",
      "Site selection and collection planning",
    ],
  },
  {
    icon: Microscope,
    title: "Provenance and Genetics",
    items: [
      "Why provenance matters",
      "Local adaptation and climate resilience",
      "Genetic diversity considerations",
      "Risks associated with inappropriate seed sourcing",
      "Future climate and restoration planning",
    ],
  },
  {
    icon: Sprout,
    title: "Field Seed Collection Techniques",
    items: [
      "Species identification",
      "Assessing seed maturity",
      "Collection methods for different species",
      "Equipment and safety requirements",
      "Recording field observations",
    ],
  },
  {
    icon: Layers,
    title: "Seed Handling and Storage",
    items: [
      "Cleaning and processing",
      "Seed storage principles",
      "Maintaining seed viability",
      "Documentation and traceability",
    ],
  },
  {
    icon: Database,
    title: "Data Collection and Monitoring",
    items: [
      "Collection records",
      "GPS and mapping",
      "Provenance information",
      "Environmental observations",
      "Digital field data systems",
    ],
  },
  {
    icon: Briefcase,
    title: "Restoration Readiness",
    items: [
      "Understanding species lists",
      "Restoration project planning",
      "Seed supply forecasting",
      "Working with nurseries and restoration practitioners",
    ],
  },
];

const OUTCOMES = [
  "Native seed collection",
  "Seed handling and storage",
  "Provenance documentation",
  "Field data collection",
  "Restoration planning",
  "Biodiversity monitoring",
  "Environmental reporting",
];

const CAREERS = [
  "Native Seed Collector",
  "Seed Bank Coordinator",
  "Restoration Technician",
  "Biodiversity Monitoring Officer",
  "Environmental Field Officer",
  "Ranger Program Coordinator",
  "Restoration Supervisor",
  "Carbon & Nature Repair Project Support",
];

const DELIVERY = [
  "On-Country workshops",
  "Ranger training programs",
  "Community seed collection intensives",
  "Restoration readiness training",
  "Custom training for Indigenous organisations and land management groups",
];

export default function Training() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { ...form, asset: "training-poster" });
      setDownloadUrl(data.download_url);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-training">
      <Seo
        title="Native Seed Collection & Restoration Training"
        description="On-Country training in native seed collection, provenance, restoration readiness and biodiversity monitoring — for Indigenous rangers and environmental practitioners."
        path="/training"
      />

      {/* Hero */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="stamp">Capability Building</span>
              <span className="specimen-label">Training — Indigenous Native Seed Collection &amp; Restoration</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              Restoration begins with <em className="text-eucalypt">seed.</em>
              <br />
              And seed begins with <em className="text-ochre2">skilled people.</em>
            </h1>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              Across Australia, demand for native seed is growing rapidly.
              Carbon projects, mine rehabilitation, biodiversity conservation,
              Nature Repair projects and ecological restoration all rely on
              access to high-quality native seed — collected from the right
              species, in the right place, at the right time.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              Many regions are already experiencing seed shortages, creating a
              growing demand for people with practical skills in seed
              collection, seed handling and restoration planning.
            </p>
            <p className="mt-6 font-serif italic text-2xl md:text-3xl text-eucalypt max-w-2xl">
              This training pathway introduces Indigenous rangers and
              environmental practitioners to the foundations of native seed
              collection and restoration practice.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img src={IMG.handsSeeds} alt="Hands inspecting seed during field training" className="w-full h-[440px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* What You Will Learn — modules */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">What You Will Learn</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-14 max-w-3xl leading-tight">
            Seven modules. <em className="text-eucalypt">From systems to soil.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule" data-testid="training-modules">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={m.title} className="bg-card p-8 md:p-10" data-testid={`module-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="specimen-label">Module {String(i + 1).padStart(2, "0")}</span>
                    <Icon size={20} className="text-eucalypt" />
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-5">{m.title}</h3>
                  <ul className="space-y-2 text-sm md:text-base text-ink/80 leading-relaxed">
                    {m.items.map((it) => (
                      <li key={it} className="flex gap-3">
                        <span className="text-ochre2 mt-1">·</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="section-num mb-3">Learning Outcomes</p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                Practical skills. Held by the people of Country.
              </h2>
              <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed">
                Participants leave with skills that are immediately useful in
                ranger work, restoration projects and community seed banks.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="border-y border-rule divide-y divide-rule" data-testid="outcomes">
                {OUTCOMES.map((o, i) => (
                  <li key={o} className="py-5 grid grid-cols-12 gap-4 items-baseline" data-testid={`outcome-${i}`}>
                    <span className="col-span-3 specimen-label text-eucalypt">{String(i + 1).padStart(2, "0")}</span>
                    <p className="col-span-9 font-serif text-xl md:text-2xl leading-snug">{o}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Career Pathways */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="specimen-label text-chalk/60 mb-4">Career Pathways</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-4xl mb-14">
            Training opens doors across the restoration economy.
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-chalk/15 border border-chalk/15" data-testid="careers">
            {CAREERS.map((c, i) => (
              <li
                key={c}
                className="bg-bark p-6 md:p-7 min-h-[150px] flex flex-col justify-between"
                data-testid={`career-${i}`}
              >
                <span className="specimen-label text-ochre">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-serif text-xl md:text-2xl leading-tight">{c}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="border-b border-rule">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">Why It Matters</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.04] mb-10">
            Native seed is more than a restoration input.
          </h2>
          <div className="space-y-6 text-base md:text-lg text-ink/85 leading-relaxed max-w-3xl">
            <p>
              It carries genetics, adaptation, ecological function and
              connection to Country.
            </p>
            <p>
              As investment in restoration, biodiversity and carbon projects
              continues to grow, the need for skilled seed practitioners will
              continue to increase.
            </p>
            <p className="font-serif italic text-2xl md:text-3xl text-eucalypt">
              Building Indigenous capability in native seed systems helps
              ensure that restoration outcomes are stronger, local knowledge is
              valued and more environmental investment remains connected to
              Country.
            </p>
          </div>
        </div>
      </section>

      {/* Delivery Options */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">Delivery Options</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-12 max-w-3xl leading-tight">
            We deliver training where it works best — on Country.
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule" data-testid="delivery">
            {DELIVERY.map((d, i) => (
              <li
                key={d}
                className="bg-card p-7 md:p-8 min-h-[150px] flex flex-col justify-between"
                data-testid={`delivery-${i}`}
              >
                <GraduationCap size={20} className="text-eucalypt" />
                <p className="font-serif text-xl md:text-2xl leading-tight mt-4">{d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Downloadable Poster (email gated) */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <p className="section-num mb-3">Free Download</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.04]">
              SeedKeepers Native Seed Training <em className="text-eucalypt">Poster.</em>
            </h2>
            <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed max-w-xl">
              A single-page overview of the program — modules, learning
              outcomes, career pathways and delivery options. Print, share or
              hand around at community meetings.
            </p>
            <div className="mt-8 border border-rule p-3 bg-cream inline-block">
              <img
                src="https://customer-assets.emergentagent.com/job_seedkeepers-au/artifacts/bqidgnav_SeedKeepers%20Seed%20Training%20Poster.png"
                alt="Native Seed Training poster preview"
                className="w-full max-w-md h-auto"
              />
            </div>
          </div>

          <div className="lg:col-span-6">
            {downloadUrl ? (
              <div className="border border-eucalypt p-10 bg-card" data-testid="poster-download-ready">
                <Check className="text-eucalypt mb-6" size={32} />
                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">Thank you.</h3>
                <p className="text-sm text-muted leading-relaxed mb-8">
                  Your download is ready. We may follow up with one short
                  message about upcoming training. We won't share your email.
                </p>
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download="SeedKeepers-Native-Seed-Training-Poster.png"
                  className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-7 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark transition-colors"
                  data-testid="poster-download-link"
                >
                  <Download size={14} /> Download the Poster
                </a>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="border border-rule p-8 md:p-10 bg-card space-y-6"
                data-testid="poster-form"
              >
                <div>
                  <p className="specimen-label mb-2">Get the poster</p>
                  <p className="font-serif text-2xl md:text-3xl leading-snug">
                    Tell us where to send the link and we'll keep you informed
                    about upcoming training.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="specimen-label">Full name</label>
                    <input
                      required
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      className="field-input"
                      data-testid="poster-name"
                    />
                  </div>
                  <div>
                    <label className="specimen-label">Email</label>
                    <input
                      required
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      className="field-input"
                      data-testid="poster-email"
                    />
                  </div>
                  <div>
                    <label className="specimen-label">Organisation</label>
                    <input
                      name="organisation"
                      value={form.organisation}
                      onChange={onChange}
                      className="field-input"
                      placeholder="Optional"
                      data-testid="poster-organisation"
                    />
                  </div>
                  <div>
                    <label className="specimen-label">Role</label>
                    <input
                      name="role"
                      value={form.role}
                      onChange={onChange}
                      className="field-input"
                      placeholder="e.g. Ranger Coordinator"
                      data-testid="poster-role"
                    />
                  </div>
                </div>
                {error && (
                  <p className="text-terracotta text-sm font-mono" data-testid="poster-error">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-7 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all disabled:opacity-60"
                  data-testid="poster-submit"
                >
                  {busy ? "Preparing…" : "Download the Poster"} <Download size={14} />
                </button>
                <p className="text-[10px] tracking-widest uppercase text-muted font-mono">
                  We respect Indigenous Data Sovereignty. Your details are stored securely and never shared.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="section-num mb-8">Next</p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
            Bring native seed and restoration training to your <em className="text-eucalypt">rangers, community or program.</em>
          </h2>
          <div className="mt-12">
            <Link
              to="/contact"
              state={{ subject: "Indigenous Native Seed Collection & Restoration Training" }}
              data-testid="training-contact-cta"
              className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            >
              Enquire about training <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
